// src/components/Register.js
import React, { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import Header from './Header'
import '../styles/Register.css'
import '../styles/Transport.css'
// Import icons from react-icons
import { FaInfoCircle, FaEye, FaEyeSlash, FaUpload, FaWhatsapp, FaSms, FaPencilAlt, FaCheck, FaTimes, FaMapMarkerAlt } from 'react-icons/fa'
import axios from 'axios'
import { T } from './Translation'
import { useLanguage } from '../context/LanguageContext'
import { translations } from '../translations/translations'
import PayMe3DSCheckout from './Payme/PayMe3DSCheckout'
// Define BASE_URL for API requests
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'

// Plan configurations with prices
const PLANS = {
  sms: {
    name: 'sms',
    priceILS: 229
  },
  whatsapp: {
    name: 'whatsapp',
    priceILS: 259
  },
};
// Component for input with tooltip
const InputWithTooltip = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  tooltip,
  prefix,
  min,
  max,
  translationKey,
  tooltipKey,
  error,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const toggleTooltip = () => {
    setIsTooltipVisible(!isTooltipVisible)
  }

  return (
    <div className="input-tooltip-wrapper">
      {label && <label className="input-label">{label}</label>}
      <div className="label-tooltip">
        {prefix ? (
          <div className="input-with-prefix">
            <span className="input-prefix">{prefix}</span>
            <input
              type={type}
              placeholder={
                translationKey ? <T k={translationKey} /> : placeholder
              }
              value={value}
              onChange={onChange}
              aria-label={translationKey ? <T k={translationKey} /> : label}
              className="input-with-prefix-field"
              min={min}
              max={max}
            />
            {(tooltip || tooltipKey) && (
              <div className="tooltip-container tooltip-container--inline">
                <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
                <span className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}>
                  {tooltipKey ? <T k={tooltipKey} /> : tooltip}
                </span>
              </div>
            )}
          </div>
        ) : (
          <input
            type={type}
            placeholder={
              translationKey ? <T k={translationKey} /> : placeholder
            }
            value={value}
            onChange={onChange}
            aria-label={translationKey ? <T k={translationKey} /> : label}
            min={min}
            max={max}
          />
        )}
        {!prefix && (tooltip || tooltipKey) && (
          <div className="tooltip-container">
            <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
            <span className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}>
              {tooltipKey ? <T k={tooltipKey} /> : tooltip}
            </span>
          </div>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  )
}

// Component for textarea with tooltip and label
const TextareaWithTooltip = ({
  label,
  placeholder,
  value,
  onChange,
  tooltip,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const toggleTooltip = () => {
    setIsTooltipVisible(!isTooltipVisible)
  }

  return (
    <div className="message-input-container">
      <label className="input-label">{label}</label>
      <div className="input-tooltip-wrapper">
        <div className="label-tooltip">
          <textarea
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            aria-label={label}
          />
          <div className="tooltip-container">
            <FaInfoCircle className="info-icon" onClick={toggleTooltip} />
            <span
              className={`tooltip-text ${isTooltipVisible ? 'visible' : ''}`}
            >
              {tooltip}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Verification code input component
const VerificationCodeInput = ({ length = 6, onComplete }) => {
  const [code, setCode] = useState(Array(length).fill(''))
  const inputs = useRef([])

  const handleChange = (e, index) => {
    const value = e.target.value
    // Only allow numeric input
    if (/[^0-9]/.test(value)) return

    // Update the code array with the new value
    const newCode = [...code]
    newCode[index] = value.substring(0, 1)
    setCode(newCode)

    // Build the complete code
    const completeCode = newCode.join('')

    // If we have a digit and it's not the last input, focus next input
    if (value && index < length - 1) {
      inputs.current[index + 1].focus()
    }

    // If we've filled all inputs, trigger the onComplete callback
    if (completeCode.length === length) {
      onComplete(completeCode)
    }
  }

  const handleKeyDown = (e, index) => {
    // If backspace is pressed and current input is empty, focus previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1].focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').trim()
    if (!/^\d+$/.test(pastedData)) return // Only allow numeric pastes

    const newCode = [...code]
    for (let i = 0; i < Math.min(length, pastedData.length); i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)

    // Focus the appropriate input box after paste
    const focusIndex = Math.min(length - 1, pastedData.length)
    inputs.current[focusIndex].focus()

    // If we've filled all inputs with the paste, trigger onComplete
    if (pastedData.length >= length) {
      onComplete(pastedData.substring(0, length))
    }
  }

  return (
    <div className="verification-code-container">
      {code.map((digit, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={index === 0 ? handlePaste : undefined}
          ref={(el) => (inputs.current[index] = el)}
          className="verification-code-input"
          autoFocus={index === 0}
        />
      ))}
    </div>
  )
}

function Register() {
  // Add navigation hook
  const { language } = useLanguage()
  const latest = useRef({}) // never triggers re-renders
  useEffect(() => {
    latest.current = {
      step,
      wifeName,
      husbandName,
      email,
      phone,
      eventType,
      mapLink,
      eventDate,
      eventTime,
      bitLink,
      payboxLink,
      organizeTransport,
      transportStops,
      paymentMethod,
      username,
      password,
      confirmPassword,
      rsvpText,
      tomorrowMessage,
      rsvpTextSecond,
      afterWeddingText,
      selectedPlan,
      messagingChannel,
      freeText,
      eventOrganizer,
    }
  }) // runs after every render

  // Keep track of which step we're on

  // Form fields
  const draft = (() => {
    try {
      const raw = localStorage.getItem('wizard-draft')
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  })()

  // 2️⃣ initialise each piece of state with the draft’s value or a default
  const [step, setStep] = useState(draft.step || 1)
  const [wifeName, setWifeName] = useState(draft.wifeName || '')
  const [husbandName, setHusbandName] = useState(draft.husbandName || '')
  const [email, setEmail] = useState(draft.email || '')
  const [phone, setPhone] = useState(draft.phone || '')
  const [eventType, setEventType] = useState(draft.eventType || 'חתונה')
  const [mapLink, setMapLink] = useState(draft.mapLink || '')
  const [eventDate, setEventDate] = useState(draft.eventDate || '')
  const [eventTime, setEventTime] = useState(draft.eventTime || '')
  const [bitLink, setBitLink] = useState(draft.bitLink || '')
  const [payboxLink, setPayboxLink] = useState(draft.payboxLink || '')
  const [organizeTransport, setOrganizeTransport] = useState(
    draft.organizeTransport ?? false
  )
  const [transportStops, setTransportStops] = useState(
    draft.transportStops || []
  )
  const [paymentMethod, setPaymentMethod] = useState(draft.paymentMethod || '')
  const [username, setUsername] = useState(draft.username || '')
  const [password, setPassword] = useState(draft.password || '')
  const [confirmPassword, setConfirmPassword] = useState(
    draft.confirmPassword || ''
  )

  const [invitationImage, setInvitationImage] = useState(null)

  const [newStop, setNewStop] = useState('')
  const [editingStopIndex, setEditingStopIndex] = useState(null)
  const [editStopValue, setEditStopValue] = useState('')

  const [showPassword, setShowPassword] = useState(false)
  /* ----- NEW ----- */
  const [rsvpText, setRsvpText] = useState(
    draft.rsvpText || 'היי! נשמח לאשר את הגעתכם לחתונתנו דרך הקישור: '
  )
  const [tomorrowMessage, setTomorrowMessage] = useState(
    draft.tomorrowMessage || 'היי! רק רצינו להזכיר לכם את החתונה מחר. נשמח לראותכם!'
  )
  const [rsvpTextSecond, setRsvpTextSecond] = useState(
    draft.rsvpTextSecond || 'תזכורת אחרונה! אנא אשרו הגעה בלחיצה על הקישור: '
  )
  const [afterWeddingText, setAfterWeddingText] = useState(
    draft.afterWeddingText ||
      'תודה שחגגתם איתנו! הייתם חלק בלתי נשכח מהיום המיוחד שלנו ❤️'
  )
  const [messagingChannel, setMessagingChannel] = useState(
    draft.messagingChannel || 'sms'
  )
  const [freeText, setFreeText] = useState(draft.freeText || '')
  const [eventOrganizer, setEventOrganizer] = useState(draft.eventOrganizer || '')

  // Field-level validation errors and step-validation loading state
  const [fieldErrors, setFieldErrors] = useState({})
  const [validating, setValidating] = useState(false)

  const setFieldError = (field, msg) =>
    setFieldErrors((prev) => ({ ...prev, [field]: msg }))
  const clearFieldError = (field) =>
    setFieldErrors((prev) => { const next = { ...prev }; delete next[field]; return next })

  // Additional state variables
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [imageUploadError, setImageUploadError] = useState('')
  const [couponCode, setCouponCode] = useState('')
  const [couponValid, setCouponValid] = useState(false)
  const [couponIsFree, setCouponIsFree] = useState(false)
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState('')


  // ----new----
  // Payment iframe state 
  const [paymentUrl, setPaymentUrl] = useState('')
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  // ----new----

  // Plan selection and payment method state
  const [selectedPlan, setSelectedPlan] = useState(draft.selectedPlan || '')
  const [paymentChoice, setPaymentChoice] = useState('') // 'coupon' or 'card'

  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [remainingTime, setRemainingTime] = useState(60)
  const [canResend, setCanResend] = useState(false)

  // New state for tracking verification success
  const [verificationSuccess, setVerificationSuccess] = useState(false)
  const [codeResetKey, setCodeResetKey] = useState(0)

  // Clear any existing errors on mount
  useEffect(() => {
    setError('')
  }, [])

  // Debounced save to localStorage on every state change (captures cleared fields)
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('wizard-draft', JSON.stringify(latest.current))
    }, 400)
    return () => clearTimeout(timer)
  })

  // Save immediately on unmount (SPA navigation) and browser unload
  useEffect(() => {
    const save = () => localStorage.setItem('wizard-draft', JSON.stringify(latest.current))
    window.addEventListener('beforeunload', save)
    return () => {
      save()
      window.removeEventListener('beforeunload', save)
    }
  }, [])

  // Timer effect for resend countdown
  useEffect(() => {
    let timer
    if (showVerificationModal && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [showVerificationModal, remainingTime])

  // Normalize phone to full international format (+972XXXXXXXXX)
  const fullPhone = (p) => {
    const digits = (p || '').trim().replace(/\D/g, '')
    if (digits.startsWith('972')) return `+${digits}`
    if (digits.startsWith('5') && digits.length === 9) return `+972${digits}`
    return `+${digits}`
  }

  // Step-level validity — mirrors the validation in each handleXNext function
  const isStep2Valid = (() => {
    if (eventType === 'חתונה') {
      if (!wifeName.trim() || wifeName.trim().split(/\s+/).length < 2) return false
      if (!husbandName.trim() || husbandName.trim().split(/\s+/).length < 2) return false
    } else {
      if (!eventOrganizer.trim() || eventOrganizer.trim().split(/\s+/).length < 2) return false
    }
    const atIndex = email.indexOf('@')
    if (atIndex <= 0 || !email.slice(atIndex + 1).includes('.')) return false
    const phoneDigits = phone.trim().replace(/\D/g, '')
    if (!phoneDigits.startsWith('5') || phoneDigits.length !== 9) return false
    return true
  })()

  const isStep3Valid = mapLink.trim() !== '' && eventDate !== ''

  const isStep7Valid =
    username.trim() !== '' &&
    password !== '' &&
    confirmPassword !== '' &&
    password === confirmPassword
  // Helper to go to the next or previous step
  const nextStep = () => setStep(step + 1)
  const prevStep = () => setStep(step - 1)

  // Build saleInfo for coupon payment (210 ILS)
  const buildCouponSaleInfo = () => {
    let payerFirstName, payerLastName
    if (eventType === 'חתונה') {
      const wifeNames = wifeName.trim().split(' ')
      const husbandNames = husbandName.trim().split(' ')
      payerFirstName = wifeNames[0] || husbandNames[0] || 'Guest'
      payerLastName = wifeNames.slice(1).join(' ') || husbandNames.slice(1).join(' ') || ''
    } else {
      const orgNames = eventOrganizer.trim().split(' ')
      payerFirstName = orgNames[0] || 'Guest'
      payerLastName = orgNames.slice(1).join(' ') || ''
    }
    const productName = language === 'he' ? 'Invitly - מחיר מיוחד בקופון' : 'Invitly - Coupon Special Price'
    return {
      seller_payme_id: process.env.REACT_APP_PAYME_SELLER_ID,
      sale_price: 35100, // 351 ILS in agorot
      currency: 'ILS',
      product_name: productName,
      payerFirstName,
      payerLastName,
      payerEmail: email || 'guest@invitly.com',
      payerPhone: phone ? fullPhone(phone) : '+972500000000',
      total: {
        amount: { value: '351.00', currency: 'ILS' },
        label: productName,
      },
    }
  }

  // Build saleInfo dynamically based on user data and selected plan
  const buildSaleInfo = () => {
    if (!selectedPlan) return null

    const plan = PLANS[selectedPlan]
    const priceInAgorot = plan.priceILS * 100 // PayMe expects price in agorot (cents)
    const priceFormatted = plan.priceILS.toFixed(2)

    // Parse names
    let payerFirstName, payerLastName
    if (eventType === 'חתונה') {
      const wifeNames = wifeName.trim().split(' ')
      const husbandNames = husbandName.trim().split(' ')
      payerFirstName = wifeNames[0] || husbandNames[0] || 'Guest'
      payerLastName = wifeNames.slice(1).join(' ') || husbandNames.slice(1).join(' ') || ''
    } else {
      const orgNames = eventOrganizer.trim().split(' ')
      payerFirstName = orgNames[0] || 'Guest'
      payerLastName = orgNames.slice(1).join(' ') || ''
    }
    
    const productName = language === 'he'
      ? `תוכנית ${plan.name === 'sms' ? 'SMS' : 'WhatsApp'} - Invitly`
      : `${plan.name.toUpperCase()} Plan - Invitly`
    
    return {
      seller_payme_id: process.env.REACT_APP_PAYME_SELLER_ID,
      sale_price: priceInAgorot,
      currency: 'ILS',
      product_name: productName,
      payerFirstName: payerFirstName,
      payerLastName: payerLastName,
      payerEmail: email || 'guest@invitly.com',
      payerPhone: phone ? fullPhone(phone) : '+972500000000',
      total: {
        amount: { value: priceFormatted, currency: 'ILS' },
        label: productName
      }
    }
  }

  /* --------------------------------------
      HANDLE REGISTRATION
  -------------------------------------- */
  const handleCouponValidation = async () => {
    setCouponLoading(true)
    setCouponError('')
    setCouponValid(false)
    setCouponIsFree(false)

    try {
      const response = await axios.post(`${BASE_URL}/validate-coupon`, {
        couponCode: couponCode,
      })

      if (response.data.valid) {
        setCouponValid(true)
        setCouponIsFree(response.data.free === true)
        setCouponError('')
      } else {
        setCouponError(language === 'he' ? 'קוד קופון לא תקין' : 'Invalid coupon code')
        setCouponValid(false)
      }
    } catch (error) {
      console.error('Error validating coupon:', error)
      setCouponError(language === 'he' ? 'שגיאה באימות הקופון. נסה שוב.' : 'Error validating coupon. Please try again.')
      setCouponValid(false)
    } finally {
      setCouponLoading(false)
    }
  }

  const handleCompleteRegistration = async () => {
    setLoading(true)
    setError('')

    try {
      // Upload invitation image directly to S3 if provided, bypassing Lambda payload limit
      let invitationUrl = null
      if (invitationImage) {
        const ext = invitationImage.name.includes('.')
          ? '.' + invitationImage.name.split('.').pop()
          : '.jpg'
        const contentType = invitationImage.type || 'image/jpeg'

        const { data: uploadData } = await axios.get(
          `${BASE_URL}/register/invitation-upload-url`,
          { params: { ext, contentType } }
        )

        await axios.put(uploadData.uploadUrl, invitationImage, {
          headers: { 'Content-Type': contentType },
        })

        invitationUrl = uploadData.s3ObjectUrl
      }

      const nameFields = eventType === 'חתונה'
        ? (() => {
            const wife = wifeName.split(' ')
            const husband = husbandName.split(' ')
            return {
              wife_firstname: wife[0],
              wife_lastname: wife[1],
              husband_firstname: husband[0],
              husband_lastname: husband[1],
            }
          })()
        : { event_organizer: eventOrganizer }

      const payload = {
        ...nameFields,
        username,
        email,
        phone_number: fullPhone(phone),
        event_date: eventDate,
        event_address: mapLink,
        event_type: eventType,
        password,
        rsvp_text: rsvpText,
        tomorrow_message: tomorrowMessage,
        rsvp_second_text: rsvpTextSecond,
        after_wedding_text: afterWeddingText,
        messaging_channel: messagingChannel,
        free_text: freeText,
        organize_transport: organizeTransport,
        ...(organizeTransport && transportStops.length > 0
          ? { transport_stops: JSON.stringify(transportStops) }
          : {}),
        ...(invitationUrl ? { invitation_url: invitationUrl } : {}),
      }

      const response = await axios.post(`${BASE_URL}/admin/register`, payload)

      if (response.status === 200) {
        localStorage.removeItem('wizard-draft')
        setShowVerificationModal(true)
        setRemainingTime(60)
        setCanResend(false)
        sendCode() // Auto-send verification code
      }
    } catch (err) {
      console.error('Registration error:', err)
      
      // Parse error message from response
      let errorMessage = language === 'he' 
        ? 'ההרשמה נכשלה. אנא בדוק את הפרטים שלך.'
        : 'Registration failed. Please check your details.'
      
      if (err.response?.data) {
        const errorData = err.response.data

        if (errorData.error === 'duplicate') {
          const field = errorData.field
          if (field === 'phone') {
            errorMessage = language === 'he'
              ? 'מספר הטלפון הזה כבר רשום במערכת. אנא השתמש במספר טלפון אחר או התחבר לחשבון הקיים שלך.'
              : 'This phone number is already registered. Please use a different phone number or sign in to your existing account.'
          } else if (field === 'email') {
            errorMessage = language === 'he'
              ? 'כתובת האימייל הזו כבר רשומה במערכת. אנא השתמש בכתובת אימייל אחרת או התחבר לחשבון הקיים שלך.'
              : 'This email address is already registered. Please use a different email or sign in to your existing account.'
          } else if (field === 'username') {
            errorMessage = language === 'he'
              ? 'שם המשתמש הזה כבר תפוס. אנא בחר שם משתמש אחר.'
              : 'This username is already taken. Please choose a different username.'
          } else {
            errorMessage = language === 'he'
              ? 'הפרטים שהזנת כבר קיימים במערכת. אנא בדוק את המידע שלך.'
              : 'Some of the details you entered are already in use. Please check your information.'
          }
        } else if (errorData.missing?.length) {
          errorMessage = language === 'he'
            ? `שדות חסרים: ${errorData.missing.join(', ')}`
            : `Missing fields: ${errorData.missing.join(', ')}`
        } else if (errorData.error) {
          errorMessage = language === 'he'
            ? `שגיאה: ${errorData.error}`
            : `Error: ${errorData.error}`
        }
      }
      
      setError(errorMessage)
    }
    setLoading(false)
  }

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Function to send verification code
  const sendCode = async () => {
    setRemainingTime(60)
    setCanResend(false)
    setCodeResetKey((k) => k + 1)

    try {
      await axios.post(`${BASE_URL}/admin/send-code`, { username })
      setError('')
      // Silently succeed - user already sees the verification modal
    } catch (err) {
      setError('Failed to send verification code. Please try again.')
      console.error('Error sending verification code:', err)
    }
  }

  // Handle verification code submission
  const handleVerificationSubmit = async (code) => {
    try {
      const response = await axios.post(`${BASE_URL}/admin/verify-code`, {
        username,
        code,
      })

      if (response.status === 200) {
        // Set verification success to true instead of closing modal
        setVerificationSuccess(true)
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.')
      console.error('Verification error:', err)
    }
  }

  // Navigate to dashboard
  const goToDashboard = () => {
    window.open('https://home.invitly.org/welcome', '_blank')
    setShowVerificationModal(false)
  }

  // Add transport stop function
  const addTransportStop = () => {
    if (newStop.trim()) {
      setTransportStops([...transportStops, newStop.trim()])
      setNewStop('')
    }
  }

  // Remove transport stop
  const removeTransportStop = (index) => {
    const updatedStops = [...transportStops]
    updatedStops.splice(index, 1)
    setTransportStops(updatedStops)
  }

  // Edit a transport stop
  const startEditStop = (index) => {
    setEditingStopIndex(index)
    setEditStopValue(transportStops[index])
  }
  const saveEditStop = () => {
    if (editStopValue.trim() && editingStopIndex !== null) {
      const updated = [...transportStops]
      updated[editingStopIndex] = editStopValue.trim()
      setTransportStops(updated)
    }
    setEditingStopIndex(null)
    setEditStopValue('')
  }
  const cancelEditStop = () => {
    setEditingStopIndex(null)
    setEditStopValue('')
  }

  // Toggle transport organization
  const toggleTransport = (value) => {
    setOrganizeTransport(value)
    if (!value) {
      setTransportStops([])
    }
  }

  // Step 1 validation: required fields + email/phone uniqueness
  const handleContactNext = async () => {
    const errors = {}

    if (eventType === 'חתונה') {
      const wifeWords = wifeName.trim().split(/\s+/)
      const husbandWords = husbandName.trim().split(/\s+/)
      if (!wifeName.trim()) errors.wifeName = language === 'he' ? 'שם הכלה נדרש' : 'Wife name is required'
      else if (wifeWords.length < 2) errors.wifeName = language === 'he' ? 'יש להזין שם פרטי ושם משפחה' : 'Please enter first and last name'
      if (!husbandName.trim()) errors.husbandName = language === 'he' ? 'שם החתן נדרש' : 'Husband name is required'
      else if (husbandWords.length < 2) errors.husbandName = language === 'he' ? 'יש להזין שם פרטי ושם משפחה' : 'Please enter first and last name'
    } else {
      const orgWords = eventOrganizer.trim().split(/\s+/)
      if (!eventOrganizer.trim()) errors.eventOrganizer = language === 'he' ? 'שם המארגן נדרש' : 'Organizer name is required'
      else if (orgWords.length < 2) errors.eventOrganizer = language === 'he' ? 'יש להזין שם פרטי ושם משפחה' : 'Please enter first and last name'
    }

    if (!email.trim()) {
      errors.email = language === 'he' ? 'אימייל נדרש' : 'Email is required'
    } else {
      const atIndex = email.indexOf('@')
      const hasValidFormat = atIndex > 0 && email.slice(atIndex + 1).includes('.')
      if (!hasValidFormat) errors.email = language === 'he' ? 'כתובת אימייל לא תקינה' : 'Invalid email address'
    }

    const phoneDigits = phone.trim().replace(/\D/g, '')
    if (!phone.trim()) {
      errors.phone = language === 'he' ? 'טלפון נדרש' : 'Phone is required'
    } else if (!phoneDigits.startsWith('5') || phoneDigits.length !== 9) {
      errors.phone = language === 'he' ? 'מספר הטלפון חייב להתחיל ב-5 ולהכיל 9 ספרות' : 'Phone must start with 5 and contain 9 digits'
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setValidating(true)
    try {
      const response = await axios.post(`${BASE_URL}/check-availability`, { email, phone })
      const taken = response.data
      const dupeErrors = {}
      if (taken.email) dupeErrors.email = language === 'he' ? 'כתובת אימייל זו כבר רשומה' : 'This email is already registered'
      if (taken.phone) dupeErrors.phone = language === 'he' ? 'מספר טלפון זה כבר רשום' : 'This phone number is already registered'
      if (Object.keys(dupeErrors).length > 0) { setFieldErrors(dupeErrors); return }
    } catch {
      // network error — allow to proceed
    } finally {
      setValidating(false)
    }
    nextStep()
  }

  // Step 2 validation: required fields only
  const handleEventDetailsNext = () => {
    const errors = {}
    if (!mapLink.trim()) errors.mapLink = language === 'he' ? 'כתובת האירוע נדרשת' : 'Event address is required'
    if (!eventDate) errors.eventDate = language === 'he' ? 'תאריך האירוע נדרש' : 'Event date is required'
    setFieldErrors(errors)
    if (Object.keys(errors).length === 0) nextStep()
  }

  // Step 5 validation: required fields + username uniqueness
  const handleAccountNext = async () => {
    const errors = {}
    if (!username.trim()) errors.username = language === 'he' ? 'שם משתמש נדרש' : 'Username is required'
    if (!password) errors.password = language === 'he' ? 'סיסמה נדרשת' : 'Password is required'
    if (!confirmPassword) errors.confirmPassword = language === 'he' ? 'אנא אשר את הסיסמה' : 'Please confirm your password'
    else if (password !== confirmPassword) errors.confirmPassword = language === 'he' ? 'הסיסמאות אינן תואמות' : 'Passwords do not match'

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setValidating(true)
    try {
      const response = await axios.post(`${BASE_URL}/check-availability`, { username })
      if (response.data.username) {
        setFieldErrors({ username: language === 'he' ? 'שם משתמש זה כבר תפוס' : 'This username is already taken' })
        return
      }
    } catch {
      // network error — allow to proceed
    } finally {
      setValidating(false)
    }
    // Plan is determined by messaging channel chosen in step 3
    setSelectedPlan(messagingChannel)
    nextStep()
  }

  return (
    <div className="app-container">
      <Helmet>
        <title>{language === 'he' ? 'צור אירוע | Invitly' : 'Create Your Event | Invitly'}</title>
        <meta name="description" content={language === 'he' ? 'צרו את האירוע שלכם וקבלו קישור אישור הגעה לשליחה לאורחים.' : 'Create your event and get an RSVP link to share with your guests.'} />
        <link rel="canonical" href="https://invitly.org/register" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />

      <div className="register-container">
        <div className="step-indicator-wrapper">
          <p className="step-progress-text">
            {language === 'he' ? 'שלב' : 'Step'}{' '}
            <span>{step}</span>{' '}
            {language === 'he' ? 'מתוך' : 'of'} 8
          </p>
          <div className="step-indicator">
            {[1,2,3,4,5,6,7,8].map(n => (
              <React.Fragment key={n}>
                {n > 1 && <div className={`step-line${step > n - 1 ? ' completed' : ''}`} />}
                <div className="step-dot-wrapper">
                  <div className={`step-dot${step === n ? ' active' : ''}${step > n ? ' completed' : ''}`}>
                    {step > n ? '✓' : n}
                  </div>
                  {(n === 5 || n === 6) && (
                    <span className="step-optional-label">
                      {language === 'he' ? 'אופציונלי' : 'optional'}
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

          {step === 1 && (
            <div className="register-card">
              <h2>{language === 'he' ? 'סוג האירוע' : 'Event Type'}</h2>
              <p>{language === 'he' ? 'בחרו את סוג האירוע שלכם.' : 'Choose the type of your event.'}</p>

              <div className="event-type-grid">
                {[
                  { value: 'חתונה', labelHe: translations.he.wedding, labelEn: translations.en.wedding },
                  { value: 'בר מצווה', labelHe: translations.he.barMitzvah, labelEn: translations.en.barMitzvah },
                  { value: 'בת מצווה', labelHe: translations.he.batMitzvah, labelEn: translations.en.batMitzvah },
                  { value: 'ברית מילה', labelHe: translations.he.britMilah, labelEn: translations.en.britMilah },
                  { value: 'חינה', labelHe: translations.he.hina, labelEn: translations.en.hina },
                  { value: 'אירוע חברה', labelHe: translations.he.jobEvent, labelEn: translations.en.jobEvent },
                  { value: 'מסיבה', labelHe: translations.he.party, labelEn: translations.en.party },
                  { value: 'יום הולדת', labelHe: translations.he.birthday, labelEn: translations.en.birthday },
                ].map(({ value, labelHe, labelEn }) => (
                  <div
                    key={value}
                    className={`event-type-card ${eventType === value ? 'selected' : ''}`}
                    onClick={() => setEventType(value)}
                    role="button"
                    aria-pressed={eventType === value}
                  >
                    <span className="event-type-card-label">
                      {language === 'he' ? labelHe : labelEn}
                    </span>
                    {eventType === value && <span className="event-type-card-check">✓</span>}
                  </div>
                ))}
              </div>

              <button className="next-btn" onClick={nextStep}>
                <T k="nextStep" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="register-card">
              <h2>
                <T k="signUpTitle" />
              </h2>
            <p>
              <T k="signUpSubtitle" />
            </p>

            {eventType === 'חתונה' ? (
              <>
                <InputWithTooltip
                  label={language === 'he' ? translations.he.wifeName : translations.en.wifeName}
                  type="text"
                  placeholder={language === 'he' ? translations.he.wifeName : translations.en.wifeName}
                  value={wifeName}
                  onChange={(e) => { setWifeName(e.target.value); clearFieldError('wifeName') }}
                  tooltip={language === 'he' ? translations.he.wifeNameTooltip : translations.en.wifeNameTooltip}
                  error={fieldErrors.wifeName}
                />
                <InputWithTooltip
                  label={language === 'he' ? translations.he.husbandName : translations.en.husbandName}
                  type="text"
                  placeholder={language === 'he' ? translations.he.husbandName : translations.en.husbandName}
                  value={husbandName}
                  onChange={(e) => { setHusbandName(e.target.value); clearFieldError('husbandName') }}
                  tooltip={language === 'he' ? translations.he.husbandNameTooltip : translations.en.husbandNameTooltip}
                  error={fieldErrors.husbandName}
                />
              </>
            ) : (
              <InputWithTooltip
                label={language === 'he' ? translations.he.eventOrganizer : translations.en.eventOrganizer}
                type="text"
                placeholder={language === 'he' ? translations.he.eventOrganizer : translations.en.eventOrganizer}
                value={eventOrganizer}
                onChange={(e) => { setEventOrganizer(e.target.value); clearFieldError('eventOrganizer') }}
                tooltip={language === 'he' ? translations.he.eventOrganizerTooltip : translations.en.eventOrganizerTooltip}
                error={fieldErrors.eventOrganizer}
              />
            )}

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.email
                  : translations.en.email
              }
              type="email"
              placeholder={
                language === 'he'
                  ? translations.he.email
                  : translations.en.email
              }
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError('email') }}
              tooltip={
                language === 'he'
                  ? translations.he.emailTooltip
                  : translations.en.emailTooltip
              }
              error={fieldErrors.email}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.phoneNumber
                  : translations.en.phoneNumber
              }
              type="tel"
              placeholder={
                language === 'he'
                  ? translations.he.phoneNumber
                  : translations.en.phoneNumber
              }
              value={phone}
              onChange={(e) => { setPhone(e.target.value); clearFieldError('phone') }}
              prefix="+972"
              error={fieldErrors.phone}
            />

            {/* Display error message if there is one */}
            {error && <div className="error-message">{error}</div>}

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button
                className="next-btn"
                onClick={handleContactNext}
                disabled={!isStep2Valid || validating}
              >
                {validating ? (language === 'he' ? 'בודק...' : 'Checking...') : <T k="nextStep" />}
              </button>
            </div>
          </div>
             )}

          {step === 3 && (
          <div className="register-card">
            <h2>
              <T k="eventDetails" />
            </h2>

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.googleMapLink
                  : translations.en.googleMapLink
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.googleMapLink
                  : translations.en.googleMapLink
              }
              value={mapLink}
              onChange={(e) => { setMapLink(e.target.value); clearFieldError('mapLink') }}
              tooltip={
                language === 'he'
                  ? translations.he.googleMapTooltip
                  : translations.en.googleMapTooltip
              }
              error={fieldErrors.mapLink}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.eventDate
                  : translations.en.eventDate
              }
              type="date"
              placeholder={
                language === 'he'
                  ? translations.he.eventDate
                  : translations.en.eventDate
              }
              value={eventDate}
              onChange={(e) => { setEventDate(e.target.value); clearFieldError('eventDate') }}
              tooltip={
                language === 'he'
                  ? translations.he.eventDateTooltip
                  : translations.en.eventDateTooltip
              }
              min={new Date().toISOString().split('T')[0]}
              error={fieldErrors.eventDate}
            />

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.eventTime
                  : translations.en.eventTime
              }
              type="time"
              placeholder={
                language === 'he'
                  ? translations.he.eventTime
                  : translations.en.eventTime
              }
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.eventTimeTooltip
                  : translations.en.eventTimeTooltip
              }
            />

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip">
                <label htmlFor="invitationImage" className="upload-label">
                  <FaUpload className="upload-icon" />
                  {language === 'he'
                    ? translations.he.uploadInvitation
                    : translations.en.uploadInvitation}
                  <span className="optional-tag">
                    {language === 'he' ? '(אופציונלי)' : '(optional)'}
                  </span>
                </label>
                <input
                  id="invitationImage"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) {
                      console.log('Selected file:', file)
                      console.log('File size:', file.size)
                      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
                      if (file.size > maxSize) {
                        setImageUploadError(
                          language === 'he' 
                            ? 'גודל הקובץ חייב להיות מתחת ל-10 מגה-בייט'
                            : 'File size must be under 10 MB'
                        )
                        e.target.value = '' // Clear the input
                        setInvitationImage(null)
                      } else {
                        setImageUploadError('') // Clear any previous errors
                        setInvitationImage(file)
                      }
                    }
                  }}
                  className="upload-input"
                />
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.uploadInvitationTooltip
                      : translations.en.uploadInvitationTooltip}
                  </span>
                </div>
              </div>
              {invitationImage && (
                <div className="image-preview-card">
                  <img
                    className="image-preview-thumb"
                    src={URL.createObjectURL(invitationImage)}
                    alt="invitation preview"
                    loading="lazy"
                  />
                  <div className="image-preview-info">
                    <span className="image-preview-name">{invitationImage.name}</span>
                    <span className="image-preview-size">
                      {(invitationImage.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <button
                    className="image-preview-remove"
                    onClick={() => setInvitationImage(null)}
                    title={language === 'he' ? 'הסר תמונה' : 'Remove image'}
                  >
                    ✕
                  </button>
                </div>
              )}
              {imageUploadError && (
                <div className="error-message">
                  {imageUploadError}
                </div>
              )}
            </div>

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button
                className="next-btn"
                onClick={handleEventDetailsNext}
                disabled={!isStep3Valid}
              >
                <T k="nextStep" />
              </button>
            </div>
          </div>
             )}
        {step === 4 && (
          <div className="register-card">
            <h2>
              {language === 'he' ? 'ערוץ תקשורת' : 'Messaging Channel'}
            </h2>
            <p>
              {language === 'he'
                ? 'בחרו איך תרצו ליצור קשר עם האורחים שלכם.'
                : 'Choose how you want to reach your guests.'}
            </p>

            <div className="channel-selection-section">
              <div className="channel-options">
                {/* SMS Option */}
                <div
                  className={`channel-card ${messagingChannel === 'sms' ? 'selected' : ''}`}
                  onClick={() => setMessagingChannel('sms')}
                  role="button"
                  aria-pressed={messagingChannel === 'sms'}
                >
                  <div className="channel-card-icon">
                    <FaSms />
                  </div>
                  <div className="channel-card-body">
                    <div className="channel-card-title">
                      {language === 'he' ? 'SMS + אתר' : 'SMS + Website'}
                    </div>
                    <div className="channel-card-desc">
                      {language === 'he'
                        ? 'האורחים מקבלים הודעת SMS עם קישור לאתר האירוע שלכם, שם הם יכולים לאשר הגעה (מגיע / לא מגיע / לא יודע) ולצפות בכל הפרטים. מי שלא ענה יקבל שיחת טלפון מסוכן AI'
                        : 'Guests receive an SMS with a link to your event website, where they can RSVP (Coming / Not Coming / Not Sure) and view all event details. Guests who don\'t respond get a follow-up phone call from an AI agent.'}
                    </div>
                  </div>
                  {messagingChannel === 'sms' && (
                    <div className="channel-card-check">✓</div>
                  )}
                </div>

                {/* WhatsApp Option */}
                <div
                  className={`channel-card ${messagingChannel === 'whatsapp' ? 'selected' : ''}`}
                  onClick={() => setMessagingChannel('whatsapp')}
                  role="button"
                  aria-pressed={messagingChannel === 'whatsapp'}
                >
                  <div className="channel-card-icon whatsapp-icon">
                    <FaWhatsapp />
                  </div>
                  <div className="channel-card-body">
                    <div className="channel-card-title">
                      {language === 'he' ? 'סוכן WhatsApp' : 'WhatsApp Agent'}
                    </div>
                    <div className="channel-card-desc">
                      {language === 'he'
                        ? 'סוכן אוטומטי ב-WhatsApp ינהל שיחה עם כל אורח: אישור הגעה, מספר אורחים, והסעה — הכל בשיחה טבעית. מי שלא ענה יקבל שיחת טלפון מסוכן AI'
                        : 'An automated WhatsApp agent handles the full conversation with each guest: RSVP, number of attendees, and transport — all in a natural chat. Guests who don\'t respond get a follow-up phone call from an AI agent.'}
                    </div>
                  </div>
                  {messagingChannel === 'whatsapp' && (
                    <div className="channel-card-check">✓</div>
                  )}
                </div>
              </div>
            </div>

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button className="next-btn" onClick={nextStep}>
                <T k="nextStep" />
              </button>
            </div>
          </div>
           )}

        {step === 5 && messagingChannel === 'whatsapp' && (
          <div className="register-card">
            <div className="card-title-row">
              <h2>{language === 'he' ? 'טקסט חופשי' : 'Free text'}</h2>
              <span className="optional-badge">
                {language === 'he' ? 'אופציונלי' : 'Optional'}
              </span>
            </div>
            <p className="optional-note">
              {language === 'he'
                ? 'טקסט שיתווסף להודעת ה-WhatsApp הראשונה לאורחים. השאירו ריק כדי לדלג. ניתן לערוך מאוחר יותר מהדשבורד.'
                : 'Extra paragraph appended to the first WhatsApp invitation. Leave empty to skip. You can edit it later from the dashboard.'}
            </p>

            <TextareaWithTooltip
              label={
                language === 'he' ? 'טקסט חופשי' : 'Free text'
              }
              placeholder={
                language === 'he'
                  ? 'לדוגמה: דרס קוד, פרטי חניה, או הודעה אישית'
                  : 'e.g. dress code, parking notes, or a personal note'
              }
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              tooltip={
                language === 'he'
                  ? 'הטקסט יופיע כפסקה נוספת בהודעת ההזמנה הראשונה ב-WhatsApp.'
                  : 'This text will appear as an extra paragraph in the first WhatsApp invitation message.'
              }
            />

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <div className="next-group">
                <button className="skip-btn" onClick={nextStep}>
                  {language === 'he' ? 'דלג' : 'Skip'}
                </button>
                <button className="next-btn" onClick={nextStep}>
                  <T k="nextStep" />
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 5 && messagingChannel !== 'whatsapp' && (
          <div className="register-card">
            <div className="card-title-row">
              <h2><T k="customizeMessages" /></h2>
              <span className="optional-badge">
                {language === 'he' ? 'אופציונלי' : 'Optional'}
              </span>
            </div>
            <p className="optional-note">
              {language === 'he'
                ? 'ניתן למלא ולערוך הודעות אלה מאוחר יותר מהדשבורד שלך.'
                : 'You can fill in and edit these messages later from your dashboard.'}
            </p>

            <div className="message-input-container">
              <label className="input-label">
                {language === 'he'
                  ? translations.he.firstRsvpMessage
                  : translations.en.firstRsvpMessage}
              </label>
              <div className="input-tooltip-wrapper">
                <div className="label-tooltip">
                  <textarea
                    placeholder={
                      language === 'he'
                        ? 'הודעת RSVP ראשונה...'
                        : 'First RSVP SMS...'
                    }
                    value={rsvpText}
                    onChange={(e) => setRsvpText(e.target.value)}
                    aria-label={
                      language === 'he'
                        ? translations.he.firstRsvpMessage
                        : translations.en.firstRsvpMessage
                    }
                  />
                  <div className="tooltip-container">
                    <FaInfoCircle className="info-icon" />
                    <span className="tooltip-text">
                      {language === 'he'
                        ? translations.he.firstRsvpTooltip
                        : translations.en.firstRsvpTooltip}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.reminderRsvpMessage
                  : translations.en.reminderRsvpMessage
              }
              placeholder={
                language === 'he'
                  ? 'הודעת תזכורת לאורחים שלא ענו...'
                  : 'Reminder SMS for unresponsive guests...'
              }
              value={rsvpTextSecond}
              onChange={(e) => setRsvpTextSecond(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.reminderRsvpTooltip
                  : translations.en.reminderRsvpTooltip
              }
            />

            <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.tomorrowMessage
                  : translations.en.tomorrowMessage
              }
              placeholder={
                language === 'he'
                  ? 'הודעת תזכורת יום לפני האירוע...'
                  : 'Reminder SMS the day before the event...'
              }
              value={tomorrowMessage}
              onChange={(e) => setTomorrowMessage(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.tomorrowTooltip
                  : translations.en.tomorrowTooltip
              }
            />

            <TextareaWithTooltip
              label={
                language === 'he'
                  ? translations.he.afterWeddingMessage
                  : translations.en.afterWeddingMessage
              }
              placeholder={
                language === 'he' ? 'הודעת תודה...' : 'Thank-you SMS...'
              }
              value={afterWeddingText}
              onChange={(e) => setAfterWeddingText(e.target.value)}
              tooltip={
                language === 'he'
                  ? translations.he.afterWeddingTooltip
                  : translations.en.afterWeddingTooltip
              }
            />

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <div className="next-group">
                <button className="skip-btn" onClick={nextStep}>
                  {language === 'he' ? 'דלג' : 'Skip'}
                </button>
                <button className="next-btn" onClick={nextStep}>
                  <T k="nextStep" />
                </button>
              </div>
            </div>
          </div>
           )}


        {step === 6 && (
          <div className="register-card">
            <div className="card-title-row">
              <h2>{language === 'he' ? 'תחבורה מאורגנת' : 'Organized Transport'}</h2>
              <span className="optional-badge">
                {language === 'he' ? 'אופציונלי' : 'Optional'}
              </span>
            </div>
            <p className="optional-note">
              {language === 'he'
                ? 'ניתן להוסיף ולערוך הסעות מאוחר יותר מהדשבורד שלך.'
                : 'You can add and edit transport stops later from your dashboard.'}
            </p>
            <p>
              {language === 'he'
                ? 'האם תרצה/י לארגן הסעה מאורגנת לאורחים?'
                : 'Would you like to organize transport for guests?'}
            </p>

            <div className="transport-options">
              <button
                className={organizeTransport ? 'active' : ''}
                onClick={() => toggleTransport(true)}
              >
                {language === 'he' ? 'כן' : 'Yes'}
              </button>
              <button
                className={!organizeTransport ? 'active' : ''}
                onClick={() => toggleTransport(false)}
              >
                {language === 'he' ? 'לא' : 'No'}
              </button>
            </div>

            {organizeTransport && (
              <div className="transport-stops-section">
                <div className="stops-header">
                  <span className="stops-label">
                    {language === 'he' ? 'תחנות עצירה' : 'Pickup stops'}
                  </span>
                  {transportStops.length > 0 && (
                    <span className="stops-count">{transportStops.length}</span>
                  )}
                </div>

                <div className="add-stop-container">
                  <div className="stop-input-wrapper">
                    <FaMapMarkerAlt className="stop-input-icon" />
                    <input
                      type="text"
                      value={newStop}
                      onChange={(e) => setNewStop(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addTransportStop()}
                      placeholder={
                        language === 'he'
                          ? 'שם התחנה...'
                          : 'Stop name...'
                      }
                    />
                  </div>
                  <button className="add-stop-btn" onClick={addTransportStop}>
                    {language === 'he' ? '+ הוסף' : '+ Add'}
                  </button>
                </div>

                {transportStops.length > 0 && (
                  <ul className="stops-list">
                    {transportStops.map((stop, index) => (
                      <li key={index} className="stop-item">
                        <span className="stop-number">{index + 1}</span>

                        {editingStopIndex === index ? (
                          <>
                            <input
                              className="stop-edit-input"
                              value={editStopValue}
                              onChange={(e) => setEditStopValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditStop()
                                if (e.key === 'Escape') cancelEditStop()
                              }}
                              autoFocus
                            />
                            <div className="stop-actions">
                              <button className="stop-save-btn" onClick={saveEditStop} title={language === 'he' ? 'שמור' : 'Save'}>
                                <FaCheck />
                              </button>
                              <button className="stop-cancel-btn" onClick={cancelEditStop} title={language === 'he' ? 'בטל' : 'Cancel'}>
                                <FaTimes />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="stop-name">{stop}</span>
                            <div className="stop-actions">
                              <button className="stop-edit-btn" onClick={() => startEditStop(index)} title={language === 'he' ? 'ערוך' : 'Edit'}>
                                <FaPencilAlt />
                              </button>
                              <button className="stop-remove-btn" onClick={() => removeTransportStop(index)} title={language === 'he' ? 'הסר' : 'Remove'}>
                                <FaTimes />
                              </button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {transportStops.length === 0 && (
                  <p className="stops-empty">
                    {language === 'he' ? 'טרם נוספו תחנות' : 'No stops added yet'}
                  </p>
                )}
              </div>
            )}

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <div className="next-group">
                <button className="skip-btn" onClick={nextStep}>
                  {language === 'he' ? 'דלג' : 'Skip'}
                </button>
                <button className="next-btn" onClick={nextStep}>
                  <T k="nextStep" />
                </button>
              </div>
            </div>
          </div>
             )}

        {step === 7 && (
          <div className="register-card">
            <h2>
              <T k="createAccount" />
            </h2>
            <p>
              <T k="accountSetup" />
            </p>

            <InputWithTooltip
              label={
                language === 'he'
                  ? translations.he.username
                  : translations.en.username
              }
              type="text"
              placeholder={
                language === 'he'
                  ? translations.he.username
                  : translations.en.username
              }
              value={username}
              onChange={(e) => { setUsername(e.target.value); clearFieldError('username') }}
              tooltip={
                language === 'he'
                  ? translations.he.usernameTooltip
                  : translations.en.usernameTooltip
              }
              error={fieldErrors.username}
            />

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={
                    language === 'he'
                      ? translations.he.password
                      : translations.en.password
                  }
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearFieldError('password') }}
                  aria-label={
                    language === 'he'
                      ? translations.he.password
                      : translations.en.password
                  }
                />
                <div
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.passwordTooltip
                      : translations.en.passwordTooltip}
                  </span>
                </div>
              </div>
              {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
            </div>

            <div className="input-tooltip-wrapper">
              <div className="label-tooltip password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={
                    language === 'he'
                      ? translations.he.confirmPassword
                      : translations.en.confirmPassword
                  }
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword') }}
                  aria-label={
                    language === 'he'
                      ? translations.he.confirmPassword
                      : translations.en.confirmPassword
                  }
                />
                <div
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                <div className="tooltip-container">
                  <FaInfoCircle className="info-icon" />
                  <span className="tooltip-text">
                    {language === 'he'
                      ? translations.he.confirmPasswordTooltip
                      : translations.en.confirmPasswordTooltip}
                  </span>
                </div>
              </div>
              {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
            </div>

            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
              <button
                className="next-btn"
                onClick={handleAccountNext}
                disabled={!isStep7Valid || validating}
              >
                {validating ? (language === 'he' ? 'בודק...' : 'Checking...') : <T k="nextStep" />}
              </button>
            </div>
          </div>
           )}


        {step === 8 && (
          <div className="register-card">
            <h2>
              <T k="paymentTitle" />
            </h2>

            {/* Plan auto-determined from messaging channel — show summary */}
            {selectedPlan && (
              <div className="payment-choice-section">
                <div className="selected-plan-info">
                  <p>
                    {language === 'he' ? 'תוכנית נבחרה: ' : 'Selected plan: '}
                    <strong>
                      {selectedPlan === 'sms' ? 'SMS' : 'WhatsApp'}
                    </strong>
                    {' - '}
                    {selectedPlan === 'sms' ? '₪229' : '₪259'}
                  </p>
                </div>

                <p className="payment-choice-title">
                  {language === 'he' ? 'בחר אמצעי תשלום:' : 'Choose payment method:'}
                </p>
                <div className="payment-options">
                  <button 
                    className={`payment-option-btn coupon-option ${paymentChoice === 'coupon' ? 'selected' : ''}`}
                    onClick={() => {
                      setPaymentChoice('coupon')
                      // Reset card-related state if switching from card
                    }}
                  >
                    {language === 'he' ? 'יש לי קופון' : 'I have a coupon'}
                  </button>
                  <button 
                  // remove to enable card payments
                  disabled={true}
                    className={`payment-option-btn card-option ${paymentChoice === 'card' ? 'selected' : ''}`}
                    onClick={() => {
                      setPaymentChoice('card')
                      // Reset coupon-related state if switching from coupon
                      setCouponValid(false)
                      setCouponIsFree(false)
                      setCouponCode('')
                      setCouponError('')
                    }}
                  >
                    {language === 'he' ? 'תשלום בכרטיס אשראי' : 'Pay with Credit Card'}
                  </button>
                </div>

                {/* Coupon Payment Option */}
                {paymentChoice === 'coupon' && (
                  <div className="coupon-payment-section">
                    <div className="coupon-section">
                      <p className="coupon-label">
                        {language === 'he' ? 'קוד קופון' : 'Coupon Code'}
                      </p>
                      <div className="coupon-input-container">
                        <input
                          type="text"
                          placeholder={translations[language]?.couponCode || 'Coupon Code'}
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value)
                            setCouponValid(false)
                            setCouponError('')
                          }}
                          className="coupon-input"
                          aria-label={translations[language]?.couponCode || 'Coupon Code'}
                        />
                        <button
                          className="validate-coupon-btn"
                          onClick={handleCouponValidation}
                          disabled={!couponCode || couponLoading}
                        >
                          {couponLoading ? <T k="validating" /> : <T k="validateCoupon" />}
                        </button>
                      </div>

                      {couponError && (
                        <div className="coupon-error-message">
                          {couponError}
                        </div>
                      )}
                      {couponValid && (
                        <div className="coupon-success-message">
                          {couponIsFree
                            ? (language === 'he'
                                ? '🎉 קופון חינם! השירות ניתן לך ללא תשלום.'
                                : '🎉 Free access! This coupon gives you the service at no charge.')
                            : (language === 'he'
                                ? 'הקופון תקף! בזכות הקופון, השירות יעלה לך רק 351 ש"ח.'
                                : 'Coupon applied! Thanks to your coupon, the service will cost you only ₪351.')
                          }
                        </div>
                      )}
                    </div>

                    {couponValid && couponIsFree && (
                      <div className="free-coupon-action">
                        {error && <div className="error-message">{error}</div>}
                        <button
                          className="next-btn"
                          onClick={handleCompleteRegistration}
                          disabled={loading}
                        >
                          {loading
                            ? (language === 'he' ? 'רושם...' : 'Registering...')
                            : (language === 'he' ? 'השלם הרשמה' : 'Complete Registration')}
                        </button>
                      </div>
                    )}

                    {couponValid && !couponIsFree && (
                      <div className="card-payment-section">
                        {error && <div className="error-message">{error}</div>}
                        <PayMe3DSCheckout
                          saleDetails={buildCouponSaleInfo()}
                          onSuccess={handleCompleteRegistration}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Credit Card Payment Option */}
                {paymentChoice === 'card' && (
                  <div className="card-payment-section">
                    <PayMe3DSCheckout 
                      saleDetails={buildSaleInfo()}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Payment Error */}
            {paymentError && (
              <div className="error-message">
                {paymentError}
              </div>
            )}
            <div className="step-nav">
              <button className="prev-btn" onClick={prevStep}>
                <T k="prevStep" />
              </button>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              {!verificationSuccess ? (
                <>
                  <h2>
                    <T k="confirmationCode" />
                  </h2>
                  <p>
                    <T k="codeSentTo" /> {phone}
                  </p>

                  {error && <div className="error-message">{error}</div>}

                  <VerificationCodeInput
                    key={codeResetKey}
                    length={6}
                    onComplete={handleVerificationSubmit}
                  />

                  <div className="resend-container">
                    {canResend ? (
                      <p>
                        <T k="didntReceiveCode" />{' '}
                        <button className="resend-link" onClick={sendCode}>
                          <T k="clickHere" />
                        </button>{' '}
                        {language === 'he' ? 'לשליחה חוזרת.' : 'to send again.'}
                      </p>
                    ) : (
                      <p>
                        <T k="resendCode" />{' '}
                        <span className="countdown">{remainingTime}</span>{' '}
                        <T k="seconds" />
                      </p>
                    )}
                  </div>

                  <button
                    className="close-modal-btn"
                    onClick={() => setShowVerificationModal(false)}
                  >
                    <T k="cancel" />
                  </button>
                </>
              ) : (
                /* Success message and dashboard link */
                <div className="success-message-container">
                  <h2>
                    <T k="registrationSuccess" />
                  </h2>
                  <p>
                    <T k="registrationSuccessMessage" />
                  </p>
                  <button
                    className="dashboard-link-btn"
                    onClick={goToDashboard}
                  >
                    <T k="goToDashboard" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
         
      </div>
    </div>
  )
}

export default Register
